import { useState, useEffect } from 'react'
import { useTuningStore, getAllPresets, exportPreset } from '../store/tuningStore'
import { TonicSection } from './TonicSection'
import { PresetManagerModal } from './PresetManagerModal'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Settings, Save, Download, Plus } from 'lucide-react'
import type { Preset } from '../types'

export const TopBar = () => {
  const { activeNotes, savePreset, currentPreset, loadPreset, newGamme } = useTuningStore()
  const [presetName, setPresetName] = useState('')
  const [presets, setPresets] = useState<Preset[]>([])
  const [showPresetManager, setShowPresetManager] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    loadPresets()
  }, [])

  useEffect(() => {
    const handleFocus = () => loadPresets()
    window.addEventListener('focus', handleFocus)
    return () => window.removeEventListener('focus', handleFocus)
  }, [])

  useEffect(() => {
    if (currentPreset) {
      setPresetName(currentPreset)
    }
  }, [currentPreset])

  const loadPresets = () => {
    getAllPresets()
      .then(setPresets)
      .catch(() => setError('Erreur lors du chargement des gammes'))
  }

  const handleLoadPreset = (presetName: string) => {
    const preset = presets.find((p) => p.name === presetName)
    if (preset) {
      loadPreset(preset)
      setPresetName(preset.name)
    }
  }

  const handleSavePreset = async () => {
    if (!presetName.trim()) {
      setError('Veuillez entrer un nom')
      return
    }
    const trimmedName = presetName.trim()
    const existingPreset = presets.find((p) => p.name === trimmedName)

    if (existingPreset && existingPreset.name !== currentPreset && !confirm(`Un preset avec ce nom existe déjà. Le remplacer?`)) {
      return
    }

    try {
      await savePreset(trimmedName)
      loadPresets()
      setError(null)
    } catch (err) {
      setError('Erreur lors de la sauvegarde')
    }
  }

  const handleNewGamme = () => {
    if (!confirm('Repartir d\'une gamme vierge (temps égal, tonique C, 440Hz) ? Les réglages non sauvegardés seront perdus.')) {
      return
    }
    newGamme()
    setPresetName('')
    setError(null)
  }

  const handleExportCurrent = () => {
    if (!currentPreset) {
      setError('Aucun preset chargé')
      return
    }
    const preset = presets.find((p) => p.name === currentPreset)
    if (preset) {
      const json = exportPreset(preset)
      const blob = new Blob([json], { type: 'application/json' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `${preset.name.replace(/\s+/g, '_')}.json`
      a.click()
      URL.revokeObjectURL(url)
    }
  }

  return (
    <>
      <div className="border-b p-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-4 flex-1">
          <TonicSection />
          
          {activeNotes.size > 0 && (
            <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-md border border-primary/20">
              <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
              <span className="text-sm font-medium">
                {activeNotes.size} note{activeNotes.size > 1 ? 's' : ''} active{activeNotes.size > 1 ? 's' : ''}
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center gap-2">
          <Button
            onClick={handleNewGamme}
            size="sm"
            variant="outline"
            title="Repartir d'une gamme vierge"
          >
            <Plus className="h-4 w-4 mr-1" />
            Nouvelle gamme
          </Button>

          <div className="flex items-center gap-2">
            <Select
              value={currentPreset || ''}
              onValueChange={handleLoadPreset}
            >
              <SelectTrigger className="w-48">
                <SelectValue placeholder="Sélectionner un preset" />
              </SelectTrigger>
              <SelectContent>
                {presets.map((preset) => (
                  <SelectItem key={preset.name} value={preset.name}>
                    {preset.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="text"
              value={presetName}
              onChange={(e) => {
                setPresetName(e.target.value)
                setError(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  handleSavePreset()
                }
              }}
              placeholder="Nom du preset"
              className="w-40"
            />
            <Button
              onClick={handleSavePreset}
              size="sm"
              variant="outline"
            >
              <Save className="h-4 w-4 mr-1" />
              Sauvegarder
            </Button>
          </div>

          {currentPreset && (
            <Button
              onClick={handleExportCurrent}
              size="sm"
              variant="outline"
              title="Exporter le preset actuel"
            >
              <Download className="h-4 w-4" />
            </Button>
          )}

          <Button
            variant="outline"
            size="sm"
            onClick={() => setShowPresetManager(true)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Gérer les presets
          </Button>
        </div>
      </div>

      {error && (
        <div className="px-4 py-2 bg-destructive/10 text-destructive text-sm border-b">
          {error}
        </div>
      )}

      <PresetManagerModal
        open={showPresetManager}
        onOpenChange={setShowPresetManager}
      />
    </>
  )
}

